/**
 * API Configuration for ProCogia Telecom AI Agent
 * Handles environment-specific API endpoints
 */

export const API_CONFIG = {
  // Base API URL - development vs production  
  BASE_URL: import.meta.env.VITE_API_URL || 
           (import.meta.env.DEV ? 'http://localhost:8000' : 'https://telecom-ai-agent-v2-production.up.railway.app'),
  
  // API endpoints
  ENDPOINTS: {
    // Health check
    HEALTH: '/health',
    
    // Authentication
    AUTH: {
      LOGIN: '/api/auth/login',
      LOGOUT: '/api/auth/logout',
      REFRESH: '/api/auth/refresh',
    },
    
    // Conversations
    CONVERSATIONS: {
      CREATE: '/api/conversations',
      GET: (sessionId: string) => `/api/conversations/${sessionId}`,
      LIST: '/api/conversations',
    },
    
    // MCP Servers
    MCP: {
      SERVERS: '/api/mcp/servers',
      TOOLS: '/api/mcp/tools',
    },
    
    // Customer Service
    CUSTOMERS: {
      SEARCH: '/api/customers/search',
      GET: (customerId: string) => `/api/customers/${customerId}`,
      TICKETS: (customerId: string) => `/api/customers/${customerId}/tickets`,
    },
    
    // Support Tickets
    TICKETS: {
      CREATE: '/api/tickets',
      GET: (ticketId: string) => `/api/tickets/${ticketId}`,
      UPDATE: (ticketId: string) => `/api/tickets/${ticketId}`,
      LIST: '/api/tickets',
    },
    
    // Network Operations
    NETWORK: {
      STATUS: '/api/network/status',
      TOWERS: '/api/network/towers',
      INCIDENTS: '/api/network/incidents',
    },
    
    // Analytics
    ANALYTICS: {
      DASHBOARD: '/api/analytics/dashboard',
      METRICS: '/api/analytics/metrics',
      REPORTS: '/api/analytics/reports',
    },
  },
  
  // Socket.IO configuration
  SOCKET: {
    URL: import.meta.env.VITE_API_URL || 'https://telecom-ai-agent-v2-production.up.railway.app',
    OPTIONS: {
      transports: ['websocket', 'polling'],
      upgrade: true,
      rememberUpgrade: true,
      timeout: 20000,
      forceNew: false,
      autoConnect: true,
      withCredentials: true,
    },
  },
  
  // Request configuration
  REQUEST: {
    TIMEOUT: 30000,
    RETRY_ATTEMPTS: 3,
    RETRY_DELAY: 1000,
  },
  
  // Environment detection
  IS_DEVELOPMENT: import.meta.env.MODE === 'development',
  IS_PRODUCTION: import.meta.env.MODE === 'production',
};

/**
 * Get full API URL for an endpoint
 */
export const getApiUrl = (endpoint: string): string => {
  return `${API_CONFIG.BASE_URL}${endpoint}`;
};

/**
 * Default headers for API requests
 */
export const getDefaultHeaders = (): Record<string, string> => {
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    'Accept': 'application/json',
  };
  
  // Add auth token if available
  const token = localStorage.getItem('authToken');
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }
  
  return headers;
};

/**
 * API client configuration for axios or fetch
 */
export const API_CLIENT_CONFIG = {
  baseURL: API_CONFIG.BASE_URL,
  timeout: API_CONFIG.REQUEST.TIMEOUT,
  headers: getDefaultHeaders(),
  withCredentials: false,
};

export default API_CONFIG; 